import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { partition } from 'lodash';
import { getAcceptedMimeTypes } from '@/features/chats/model/file-upload.constants';
import { useVideoAttachmentsEnabled } from '@/features/chats/model/useVideoAttachmentsEnabled';
import { notificationService } from '@/lib/services/notification.service';

export const useFileValidation = (agentId?: string) => {
  const { t } = useTranslation('chats');
  // Determine which MIME types to accept based on agent. Video is gated behind
  // the feature flag + whether any model this agent has enabled can read one.
  const videoEnabled = useVideoAttachmentsEnabled();
  const acceptedMimeTypes = useMemo(
    () => getAcceptedMimeTypes(agentId, videoEnabled),
    [agentId, videoEnabled],
  );

  const isFileTypeAccepted = useCallback(
    (dataItem: DataTransferItem) =>
      dataItem.kind === 'file' && acceptedMimeTypes.includes(dataItem.type),
    [acceptedMimeTypes],
  );
  const getPartitionedFiles = useCallback(
    (dataTransfer: DataTransfer) => {
      const items = [...dataTransfer.items];
      return partition(items, isFileTypeAccepted);
    },
    [isFileTypeAccepted],
  );

  const hasAcceptedFiles = useCallback(
    (dataTransfer: DataTransfer) => {
      const [acceptedFiles] = getPartitionedFiles(dataTransfer);
      return acceptedFiles.length > 0;
    },
    [getPartitionedFiles],
  );

  const getValidFiles = useCallback((dataTransferItems: DataTransferItem[]) => {
    return dataTransferItems
      .map((item) => item.getAsFile())
      .filter((file): file is File => file !== null);
  }, []);

  const notifyRejectedFiles = useCallback(
    (rejectedItems: DataTransferItem[]) => {
      rejectedItems
        .filter((item) => item.kind === 'file')
        .forEach((item) => {
          const fileName = item.getAsFile()?.name || t('chats.useFileValidation.unknownFile');
          notificationService.showError(
            t('chats.useFileValidation.rejectedFileUnsupportedFormat', { fileName }),
          );
        });
    },
    [t],
  );

  return {
    getPartitionedFiles,
    hasAcceptedFiles,
    getValidFiles,
    notifyRejectedFiles,
  };
};
